import { Fragment } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { HourglassTopRounded, PictureAsPdfOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { CredentialProgress } from '../../../../lib/api/credential-progress.api';
import {
  formatGpa,
  formatPoints,
  groupByTerm,
  progressFor,
  type RecordAudience,
} from './academic-record.model';
import { useRecordPdf } from './use-record-pdf';
import { averageRange, toScale } from '../../../../lib/grading';

// ─── Expediente: lo cursado, en orden, con letra, puntos y créditos ─────────
// Lo que está EN CURSO va aparte, al final, sin nota ni créditos: todavía no
// tiene nada que informar y no cuenta para ninguna credencial.
// `audience="student"` (el portal) quita lo que es solo del registro académico,
// en pantalla y en el PDF: ver `progressFor`.

export function AcademicRecordTab({
  progress: full,
  audience = 'staff',
}: {
  progress: CredentialProgress;
  audience?: RecordAudience;
}) {
  const { t } = useTranslation('sis');
  const progress = progressFor(full, audience);
  const groups = groupByTerm(progress.rows);
  const current = groupByTerm(progress.inProgress);

  // El transcript no oficial: la misma implementación que el inicio del portal.
  const pdf = useRecordPdf(progress, audience);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="outlined" startIcon={<PictureAsPdfOutlined />} onClick={pdf.download}>
          {t('academicRecord.pdf.download')}
        </Button>
      </Stack>
      {pdf.error && <Alert severity="error">{pdf.error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Figure
          label={t('academicRecord.summary.unitsEarned')}
          value={`${progress.totals.unitsEarned} / ${progress.totals.unitsAttempted}`}
          help={[
            t('academicRecord.summary.unitsHelp'),
            t('academicRecord.summary.attempted', { count: progress.totals.unitsAttempted }),
          ]}
        />
        <Figure
          label={t('academicRecord.summary.coursesPassed')}
          value={String(progress.totals.coursesPassed)}
        />
        <Figure
          label={t('academicRecord.summary.gpa', { range: averageRange(toScale(progress.scale)) })}
          value={formatGpa(progress.totals.gpa)}
          help={[t('academicRecord.summary.gpaHelp')]}
        />
      </Stack>

      {groups.length === 0 ? (
        <Alert severity="info" variant="outlined">
          {t('academicRecord.record.empty')}
        </Alert>
      ) : (
        // UNA tabla: el período es una fila de grupo. Con datos reales casi cada
        // período trae un solo curso, y una tarjeta por período era una columna
        // de tarjetas de una fila.
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table size="small" aria-label={t('academicRecord.tabs.record')}>
              <TableHead>
                <TableRow
                  sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}
                >
                  <TableCell>{t('academicRecord.record.columns.course')}</TableCell>
                  <TableCell align="center">{t('academicRecord.record.columns.mark')}</TableCell>
                  <TableCell align="center">{t('academicRecord.record.columns.points')}</TableCell>
                  <TableCell align="center">
                    {t('academicRecord.record.columns.attempted')}
                  </TableCell>
                  <TableCell align="center">{t('academicRecord.record.columns.earned')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g, i) => (
                  <Fragment key={`${g.termId}-${i}`}>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={5} sx={{ py: 0.75 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {g.termName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((r, j) => (
                      <TableRow key={`${r.courseId}-${j}`}>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {r.courseCode}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {r.courseName}
                            </Typography>
                            {r.repeat && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('academicRecord.record.repeat')}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Stack>
                          {r.gradeNote && (
                            <Typography variant="caption" color="text.secondary">
                              {t('academicRecord.record.sourceNote', { note: r.gradeNote })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" component="span" fontWeight={700}>
                            {r.gradeLabel ?? '—'}
                          </Typography>{' '}
                          <Typography variant="caption" component="span" color="text.secondary">
                            {t(`academicRecord.outcome.${r.outcome}`)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{formatPoints(r.points)}</TableCell>
                        <TableCell align="center">{r.unitsAttempted}</TableCell>
                        <TableCell align="center">{r.unitsEarned}</TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {current.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 3, borderStyle: 'dashed' }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <HourglassTopRounded fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t('academicRecord.inProgress.title')}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1.5}>
              {t('academicRecord.inProgress.help')}
            </Typography>
            {current.map((g) => (
              <Box key={g.termId} mb={1}>
                <Typography variant="body2" fontWeight={600}>
                  {g.termName}
                </Typography>
                {g.rows.map((r) => (
                  <Typography key={r.courseId} variant="body2" color="text.secondary">
                    {r.courseCode} · {r.courseName}
                  </Typography>
                ))}
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

function Figure({ label, value, help = [] }: { label: string; value: string; help?: string[] }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, flex: 1 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
        {help.map((line) => (
          <Typography key={line} variant="caption" color="text.secondary" display="block">
            {line}
          </Typography>
        ))}
      </CardContent>
    </Card>
  );
}
